import { useContext, useState } from 'react';
import { useCallback } from 'react';
import { HimmaContext } from '../context/HimmaContext';
import {
  OnboardingSlides,
  AppDesign,
  SocialLogin,
  GoogleMaps,
  SettingsResponse,
} from '../types/settings';
import { ConfigurationSettings } from '../client-sdk/api';

export const useSettingsApi = () => {
  const api = useContext(HimmaContext)!;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const createSettings = useCallback(
    async (
      settingsData: Partial<ConfigurationSettings>
    ): Promise<SettingsResponse> => {
      setLoading(true);
      setError(null);
      try {
        const response = await api.addConfigurationSettings(settingsData);
        return response.data;
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [api]
  );

  const updateOnboardingSlides = useCallback(
    async (data: OnboardingSlides): Promise<SettingsResponse> => {
      return createSettings({ onboardingSlidesData: data });
    },
    [createSettings]
  );

  const updateAppDesign = useCallback(
    async (data: AppDesign): Promise<SettingsResponse> => {
      return createSettings({ appDesignData: data });
    },
    [createSettings]
  );

  const updateSocialLogin = useCallback(
    async (data: SocialLogin): Promise<SettingsResponse> => {
      return createSettings({ socialLoginData: data });
    },
    [createSettings]
  );

  const updateGoogleMaps = useCallback(
    async (data: GoogleMaps): Promise<SettingsResponse> => {
      return createSettings({ googleMapsData: data });
    },
    [createSettings]
  );

  const getAllSettings = useCallback(
    async (query: any) => {
      setLoading(true);
      setError(null);
      try {
        const response = await api.getConfigurationSettings(query.language);
        return response;
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [api]
  );

  return {
    createSettings,
    updateOnboardingSlides,
    updateAppDesign,
    updateSocialLogin,
    updateGoogleMaps,
    // updateBanner,
    getAllSettings,
    loading,
    error,
  };
};
